import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuote } from "@/components/QuoteProvider";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
import { ArrowRight, ArrowLeft, CheckCircle2, Phone, MessageCircle, Wrench, Scissors, Anchor, Package, Cog } from "lucide-react";

const CATEGORIES = [
  {
    key: "clamping-tools",
    label: "Clamping Tools",
    icon: Wrench,
    blurb: "BESSEY clamping tools for holding, joining and positioning workpieces in fabrication, welding, metalworking and woodworking.",
  },
  {
    key: "cutting-tools",
    label: "Cutting Tools",
    icon: Scissors,
    blurb: "BESSEY cutting tools for precise cutting operations across industrial and workshop applications.",
  },
  {
    key: "toggle-clamps",
    label: "Toggle Clamps",
    icon: Anchor,
    blurb: "BESSEY toggle clamps for fixture, jig and assembly-line workholding requirements.",
  },
  {
    key: "accessories",
    label: "Accessories",
    icon: Package,
    blurb: "BESSEY accessories to complement clamping and cutting tools in daily workshop use.",
  },
  {
    key: "spare-parts",
    label: "Spare Parts",
    icon: Cog,
    blurb: "Genuine BESSEY spare parts for maintenance and replacement across the clamping and cutting range.",
  },
];

const CAPABILITIES = [
  "Premium German clamping and cutting tools",
  "Applications: fabrication, welding, metalworking, woodworking and industrial workholding",
  "Product-selection support based on your specific application",
  "Bulk supply for workshops, OEMs and maintenance departments",
  "Delivery across Telangana and Andhra Pradesh",
];

export default function BesseyPage() {
  const { openQuote } = useQuote();
  const { settings } = useOutletContext() || { settings: {} };
  const s = settings || {};
  const phone = (s.phone || "").replace(/[^0-9]/g, "");
  const waNumber = (s.whatsapp || s.phone || "").replace(/[^0-9]/g, "");
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent("Hello SSK Enterprises, I would like to enquire about BESSEY clamping tools.")}`;

  useSeo({
    title: "BESSEY Clamps — Premium German Clamping & Cutting Tools | SSK Enterprises",
    description:
      "BESSEY clamping tools, cutting tools, toggle clamps, accessories and spare parts supplied by SSK Enterprises across Telangana and Andhra Pradesh — for fabrication, welding, metalworking and woodworking.",
    path: "/products/bessey-clamps",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Products", path: "/products" },
        { name: "BESSEY Clamps", path: "/products/bessey-clamps" },
      ]),
    ],
  });

  return (
    <>
      {/* HERO */}
      <section className="bg-[#0F2747] text-white">
        <div className="container-x py-16 md:py-24">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white mb-6" data-testid="bessey-back">
            <ArrowLeft className="w-4 h-4" /> All Products
          </Link>
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-white/70 mb-3">BESSEY Clamps</div>
            <h1 className="font-display text-4xl md:text-6xl font-black leading-tight">BESSEY Clamping & Cutting Tools</h1>
            <p className="text-lg md:text-xl text-slate-200 mt-5 leading-relaxed">
              Premium German clamping and cutting tools for fabrication, welding, metalworking, woodworking and industrial applications — supplied on customer requirement.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => openQuote({ division: "bessey", product_name: "BESSEY Clamps Enquiry" })} className="btn-accent" data-testid="bessey-hero-quote">
                Request a Quotation <ArrowRight className="w-4 h-4" />
              </button>
              <a href={waUrl} target="_blank" rel="noreferrer" className="btn-ghost-light">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
              {phone && (
                <a href={`tel:${phone}`} className="btn-ghost-light">
                  <Phone className="w-4 h-4" /> Call {s.phone}
                </a>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES + CATEGORIES */}
      <section className="section bg-slate-50 border-y border-slate-200">
        <div className="container-x grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <h2 className="font-display text-2xl md:text-3xl font-black text-[#1E293B]">What we can source</h2>
            <ul className="mt-6 space-y-3">
              {CAPABILITIES.map((c) => (
                <li key={c} className="flex gap-3 items-start">
                  <CheckCircle2 className="w-5 h-5 text-[#0F2747] flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-700 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <button onClick={() => openQuote({ division: "bessey", product_name: "BESSEY Clamps Enquiry" })} className="btn-accent" data-testid="bessey-cap-quote">
                Request a Quotation <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="lg:col-span-3">
            <div className="tech-card p-7 md:p-9" data-testid="bessey-categories-panel">
              <div className="overline">BESSEY Product Categories</div>
              <h3 className="font-display text-xl md:text-2xl font-black text-[#0F2747] leading-tight mt-2">Categories we supply</h3>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {CATEGORIES.map(({ key, label, icon: Icon, blurb }) => (
                  <li key={key} className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#B5121B]/10 text-[#B5121B] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4" strokeWidth={2.25} />
                    </div>
                    <div>
                      <div className="font-display text-[15px] font-black text-[#1E293B] leading-tight">{label}</div>
                      <div className="text-slate-600 text-[13px] leading-snug mt-1">{blurb}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-[#0F2747] text-white" data-testid="bessey-page-cta">
        <div className="container-x grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-black leading-tight">Need a BESSEY quotation?</h2>
            <p className="text-white/85 mt-4">Share your BESSEY model or application and our team will assist with product selection and prompt quotations.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <button onClick={() => openQuote({ division: "bessey", product_name: "BESSEY Clamps Enquiry" })} className="btn-accent">Request a Quotation</button>
            <a href={waUrl} target="_blank" rel="noreferrer" className="btn-ghost-light"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
          </div>
        </div>
      </section>
    </>
  );
}
